/* One product, across whichever farms a customer has.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Every entry in the rail was `staffOnly`, and `navFor` filters those out for
 * a customer -- so a customer's left menu rendered as nothing at all. The
 * comment beside it argued that "a customer has one destination, their farms,
 * and the wordmark already goes there", and that was true when the only
 * customer surface was the farm list.
 *
 * It stopped being true the moment there were two products. Listening ended up
 * reachable only from a text link at the bottom of the lice forecast page,
 * which is to say: to hear your hydrophone you first had to open a page about
 * salmon lice and scroll past a four-week forecast. Nobody would find that who
 * was not told, and nothing on the screen suggested it was there.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A HOP RATHER THAN A PAGE
 *
 * Both products are per farm, and most customers have one. Making the menu
 * item a real page would put a list of one between somebody and the thing they
 * pressed for -- a screen whose only content is the answer to a question they
 * did not ask.
 *
 * So with one farm it goes straight there, and with several it asks which,
 * which is the only case where a choice exists. The redirect replaces the
 * history entry rather than pushing one, so Back goes where the reader came
 * from instead of bouncing off this route again.
 */
import { useEffect } from 'react'
import { useApi } from '../useApi'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Link, navigate } from '../router'
import { useT } from '../lang'

interface SiteRow {
  site_id: string
  name: string
  municipality: string | null
  features?: string[]
}

export type ProductKind = 'lytt' | 'lus' | 'logg' | 'loggbok' | 'foring'
  | 'laksvel' | 'appetitt' | 'lusedagbok'

const WHERE: Record<ProductKind, {
  /** The path under a site. */ tail: string
  title: string
  lead: string
  /** The feature a farm must have bought to be offered here. Without it the
   *  picker would list farms whose page answers 403 -- the same "a navigation
   *  item that cannot work" argument the rail already makes. */
  feature?: string
}> = {
  lytt: { tail: '/lytt', title: 'Listen',
          lead: 'Which farm do you want to listen to?' },
  lus: { tail: '', title: 'Lice',
         lead: 'Which farm do you want the forecast for?' },
  logg: { tail: '/logg', title: 'Log',
          lead: 'Which farm do you want the log for?',
          feature: 'analysis.log' },
  foring: { tail: '/foring', title: 'Feeding',
            lead: 'Which farm do you want the feeding for?',
            feature: 'analysis.feeding' },
  laksvel: { tail: '/laksvel', title: 'Laksvel',
             lead: 'Which farm do you want to register welfare for?',
             feature: 'analysis.laksvel' },
  loggbok: { tail: '/loggbok', title: 'Loggbok',
             lead: 'Which farm do you want the logbook for?',
             feature: 'analysis.loggbok' },
  appetitt: { tail: '/appetitt', title: 'Appetite',
              lead: 'Which farm do you want the appetite for?',
              feature: 'analysis.appetitt' },
  // The lice product's book: the same purchase as the forecast and counting.
  lusedagbok: { tail: '/lusedagbok', title: 'Lusedagbok',
                lead: 'Hvilket anlegg er boken til?',
                feature: 'lice.forecast' },
}

export function Product({ kind }: { kind: ProductKind }) {
  const t = useT()
  const q = useApi<{ items: SiteRow[] }>('/v1/sites?limit=200')
  const where = WHERE[kind]
  const all = q.data?.items ?? []
  const items = where.feature
    ? all.filter((s) => s.features?.includes(where.feature!))
    : all
  const only = items.length === 1 ? items[0] : undefined

  /* In an effect rather than during render: navigating while React is
   * rendering is a side effect in the middle of a pure function, and it warns
   * about exactly that. One farm is the common case, so this route is usually
   * a frame of nothing followed by the destination. */
  useEffect(() => {
    if (!only) return
    navigate(`/sites/${encodeURIComponent(only.site_id)}${where.tail}`, true)
  }, [only, where.tail])

  if (q.error) return <ErrorBox error={q.error} />
  if (q.cold) return <Rows n={3} />
  if (only) return null

  if (!items.length) {
    return (
      <>
        <PageHead title={t(where.title)} />
        <p className="empty">
          {t('No farms have been assigned to you. Phoniq sets that up.')}
        </p>
      </>
    )
  }

  return (
    <>
      <PageHead title={t(where.title)} lead={t(where.lead)} />
      <div className="stack">
        {items.map((s) => (
          <Link key={s.site_id} className="card farm-pick"
            to={`/sites/${encodeURIComponent(s.site_id)}${where.tail}`}>
            <strong>{s.name}</strong>
            {s.municipality && <span className="muted">{s.municipality}</span>}
          </Link>
        ))}
      </div>
    </>
  )
}
